<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <!-- The picture is optional and belongs to this resume alone. The card is the same one the
         profile settings use: choose a photo, crop it, save. -->
    <div class="sm:col-span-2 flex items-center gap-4 pb-3 mb-1 border-b border-gray-100 dark:border-gray-800">
      <AvatarUploadCard :picture-url="pictureUrl || ''" :avatar-name="initials" :upload-url="`/api/v1/resumes/${resumeId}/picture`"
        :remove-url="`/api/v1/resumes/${resumeId}/picture`" form-field-name="resume[picture]" title="Resume photo"
        :shape="settings.picture_shape === 'circle' ? 'circle' : 'rounded'" size="w-20 h-20" avatar-color-class="bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700"
        @updated="$emit('picture-updated', $event)" @error="$emit('picture-error', $event)" />

      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Photo <span class="font-normal text-gray-400 dark:text-gray-500">(optional)</span></p>
        <p class="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">{{ pictureUrl ? 'Click the photo to change or remove it.' : 'Click the circle to add one. A square photo works best.' }}</p>

        <div v-if="pictureUrl" class="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div class="w-44"><SegmentedControl v-model="settings.picture_shape" label="Photo shape" :options="SHAPES" /></div>
          <label class="inline-flex items-center gap-1.5 text-[13px] text-gray-700 dark:text-gray-200 cursor-pointer">
            <input v-model="settings.show_picture" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
            Show on resume
          </label>
        </div>
      </div>
    </div>

    <FormField v-model="basics.full_name" label="Full name" placeholder="Ada Lovelace" />
    <FormField v-model="basics.headline" label="Headline" placeholder="Senior Software Engineer" />
    <FormField v-model="basics.email" label="Email" type="email" placeholder="ada@example.com" />
    <FormField v-model="basics.phone" label="Phone" type="tel" :maxlength="50" placeholder="+49 170 0000000" />
    <FormField v-model="basics.location" label="Location" placeholder="Berlin, Germany" />
    <FormField v-model="basics.website" label="Website" type="url" :maxlength="300" placeholder="https://" />
    <FormField v-model="basics.linkedin" label="LinkedIn" type="url" :maxlength="300" placeholder="https://linkedin.com/in/..." />
    <FormField v-model="basics.github" label="GitHub" type="url" :maxlength="300" placeholder="https://github.com/..." />

    <div class="sm:col-span-2">
      <span class="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">More links</span>
      <ul class="space-y-1.5">
        <li v-for="(link, index) in basics.links" :key="index" class="flex items-center gap-1.5">
          <input v-model="link.label" type="text" maxlength="60" placeholder="Portfolio" :aria-label="`Link ${index + 1} label`" :class="INPUT + ' w-32 flex-shrink-0'">
          <input v-model="link.url" type="url" maxlength="300" placeholder="https://" :aria-label="`Link ${index + 1} address`" :class="INPUT + ' flex-1 min-w-0'">
          <button type="button" @click="basics.links.splice(index, 1)" :aria-label="`Remove link ${index + 1}`"
            class="w-7 h-7 flex-shrink-0 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </li>
      </ul>
      <button v-if="basics.links.length < MAX_LINKS" type="button" @click="basics.links.push({ label: '', url: '' })"
        class="mt-1.5 inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
        Add a link
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FormField from './FormField.vue'
import SegmentedControl from './SegmentedControl.vue'
import AvatarUploadCard from 'controllers/vue_components/settings/shared/AvatarUploadCard.vue'

const INPUT = 'h-9 px-3 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400'
const MAX_LINKS = 8 // ResumeContent::MAX_LINKS

const SHAPES = [{ value: 'circle', label: 'Circle' }, { value: 'rounded', label: 'Rounded' }, { value: 'square', label: 'Square' }]

// The resume's own `basics` and `settings`, edited in place. The picture is not part of either:
// it is uploaded on its own, and `picture-updated` carries the resume the server sent back.
const props = defineProps({
  basics: { type: Object, required: true },
  settings: { type: Object, required: true },
  resumeId: { type: String, required: true },
  pictureUrl: { type: String, default: null },
})
defineEmits(['picture-updated', 'picture-error'])

// Two letters from the name stand in for a missing photo.
const initials = computed(() => props.basics.full_name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '+')
</script>
